/*
 * Connected applications — the screen that makes *disconnect* a thing a person
 * can do rather than a sentence in a design note.
 *
 * It is written in the shape of the fleet's settings screens (the header, the
 * bordered table, the confirm dialog before something irreversible) but it is
 * **not** a copy of one: the fleet has no OAuth grants, so there is no original
 * to align to and no fleet strings to borrow. Its words therefore live here
 * rather than in `useTranslation`, whose keys are the fleet's own English word
 * for word — inventing `connectedApplications.*` keys there would make this
 * screen's copy look like something upstream wrote ([MEM-125], [MEM-131]).
 *
 * Two things on it are decisions, not layout:
 *
 *  1. **The name is marked as unverified and the client id is printed beside
 *     it.** A client chooses its own `name` when it registers and may write
 *     "ChatGPT"; the client id is either a URL its owner has to control
 *     (`https://chatgpt.com/oauth/client.json`) or an id we issued. Ordering the
 *     screen by what cannot be forged rather than by what reads nicely is the
 *     rule the consent screen already follows ([MEM-242]).
 *  2. **Disconnecting says what it ends.** The grant is held by the account, so
 *     it ends that application's access in every workspace this person is a
 *     member of — and it takes effect on the next call rather than when a token
 *     expires, because `/mcp` asks whether the grant still stands.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import {
  useConnectedApplications,
  useDisconnectApplication,
  type ConnectedApplication,
} from "@/lib/connected-applications";
import { formatDistanceToNow } from "date-fns";
import { Unplug } from "lucide-react";
import { useState } from "react";

/**
 * What a failure is allowed to say — the rule `APITokensSettings` established.
 *
 * `api()` builds an `ApiError` out of a non-2xx answer and nothing else, so a
 * dropped connection arrives here as `TypeError: Failed to fetch`, a sentence
 * written for a developer. The API's own words are shown when they exist, and
 * anything else falls back to ours.
 */
function said(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** The name to print, and whether it is a name at all. */
function applicationLabel(application: ConnectedApplication): string {
  const named = application.name?.trim();
  return named ? named : "Unnamed application";
}

export function ConnectedApplicationsSettings() {
  const { data, isLoading, error } = useConnectedApplications();
  const disconnect = useDisconnectApplication();
  const [toDisconnect, setToDisconnect] = useState<ConnectedApplication | null>(null);

  const openDisconnectDialog = (application: ConnectedApplication | null) => {
    // The previous refusal is cleared as the dialog opens, not as it closes: a
    // dialog that opens still carrying the last failure explains a mistake
    // nobody has made yet.
    if (application) disconnect.reset();
    setToDisconnect(application);
  };

  const confirmDisconnect = () => {
    if (!toDisconnect) return;
    disconnect.mutate(toDisconnect.clientId, { onSuccess: () => setToDisconnect(null) });
  };

  const applications = data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Connected applications</h3>
        <p className="text-sm text-muted-foreground">
          Applications you allowed to use Sitebrew on your behalf, and how to take that back.
        </p>
      </div>

      <div className="rounded-lg border bg-muted/40 p-4 text-sm">
        <p className="font-medium">What disconnecting does</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>The application loses access on its next request, not when its token expires.</li>
          <li>
            Access is granted by you, not by a workspace, so disconnecting ends it in every
            workspace you are a member of.
          </li>
          <li>
            The application can ask again. Connecting it a second time brings you back to the
            consent screen.
          </li>
          <li>
            An application chooses its own name. Only the client id below says who it really is.
          </li>
        </ul>
      </div>

      {/*
        A refetch that failed while the previous answer is still in hand: the
        rows below are real grants and stay, but the screen has to say it is not
        looking at the newest list — otherwise a disconnect that did not go
        through reads as a listing that has not changed yet.
      */}
      {error && data ? (
        <p role="alert" className="text-sm text-destructive">
          {said(error, "Failed to load connected applications")}
        </p>
      ) : null}

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Application</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Connected</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || (error && !data) || applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {/*
                    The API's own sentence when the list failed, rather than
                    "No applications connected" — which is a different fact, and
                    the one somebody would act on by believing a connector they
                    are trying to remove is already gone.
                  */}
                  {isLoading
                    ? "Loading..."
                    : error
                      ? said(error, "Failed to load connected applications")
                      : "No applications connected"}
                </TableCell>
              </TableRow>
            ) : (
              applications.map((application) => (
                <ApplicationRow
                  key={application.clientId}
                  application={application}
                  onDisconnect={() => openDisconnectDialog(application)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={toDisconnect !== null}
        onOpenChange={(open) => {
          if (!open) openDisconnectDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect application</AlertDialogTitle>
            <AlertDialogDescription>
              {/*
                The client id is in the question, not only the name: the name is
                what the application called itself, and a confirm dialog that
                names only that is asking somebody to approve a claim rather
                than a fact.
              */}
              {toDisconnect
                ? `Disconnect ${applicationLabel(toDisconnect)} (${toDisconnect.clientId})? It loses access to your websites straight away. It can ask for access again.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {disconnect.error ? (
            <p role="alert" className="text-sm text-destructive">
              {said(disconnect.error, "Failed to disconnect the application")}
            </p>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnect.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnect.isPending}
              onClick={(event) => {
                /*
                 * Radix closes the dialog on this button by itself, and the
                 * refusal is drawn *inside* the dialog — so a refused disconnect
                 * would say nothing at all: the application stays connected and
                 * the only thing that happened is that a dialog went away. The
                 * close therefore becomes ours and happens on success only, the
                 * same way `APITokensSettings` handles its revoke.
                 */
                event.preventDefault();
                confirmDisconnect();
              }}
            >
              {disconnect.isPending ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ApplicationRow({
  application,
  onDisconnect,
}: {
  application: ConnectedApplication;
  onDisconnect: () => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {applicationLabel(application)}
          {/*
            Every name on this screen carries this, including one that reads
            like a company somebody trusts — especially that one. A badge shown
            only on suspicious names would be a judgement we have no way to make.
          */}
          <Badge variant="outline">unverified</Badge>
        </div>
      </TableCell>
      <TableCell className="max-w-xs break-all text-muted-foreground">
        {application.clientId}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {application.scopes.length === 0 ? (
            <span className="text-muted-foreground">None recorded</span>
          ) : (
            application.scopes.map((scope) => (
              <Badge key={scope} variant="secondary">
                {scope}
              </Badge>
            ))
          )}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDistanceToNow(new Date(application.connectedAt), { addSuffix: true })}
      </TableCell>
      <TableCell className="text-right">
        <Button variant="ghost" size="sm" onClick={onDisconnect} aria-label="Disconnect">
          <Unplug className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
